import { replace } from 'connected-react-router';

import { selectReportOrCurrentProjectId } from 'util/selector/reportSelector';
import {
  selectLanguage,
  selectProjectSearch,
  selectProjectsQuery,
} from 'util/selector/querySelector';

import {
  selectCurrentProjectId,
  selectIsPulseOrTemplateProjectKind,
  selectPulseTemplateId,
} from 'reduxStore/project/selectors';
import { pushQuery } from 'reduxStore/router/asyncActions';
import { Project } from 'model/Project';
import { queryDto } from 'api/dto/Query.dto';
import { ProjectKind } from 'model/ProjectKind';
import { QueryType } from 'model/QueryType';
import { projectUrlBuilder } from 'shared/utils/builders/projectUrlBuilder';
import { projectApi } from 'container/projectApi';
import { createAsyncThunk } from '@reduxjs/toolkit';
import { serializeError } from 'shared/utils/redux';
import { ProjectLiveSettingsModel } from 'api/dto/ProjectLiveSettingsDto';
import { FocusAreasModel } from 'api/dto/new/FocusArea.dto';

import { setPagination } from './slice';
import { ProjectState, STORE_NAME } from './initialState';

type FetchProjectsArg = { page?: number; searchOverride?: string };

export const fetchProjects = createAsyncThunk(
  `${STORE_NAME}/fetchProjects`,
  ({ page, searchOverride }: FetchProjectsArg = {}, { dispatch, getState }) => {
    const paginationQuery = selectProjectsQuery(page)(getState());
    const language = selectLanguage(getState());
    const languageQuery = language ? `&${queryDto.stringifyLanguage(language)}` : '';
    const search =
      searchOverride !== undefined ? searchOverride : selectProjectSearch(getState()) ?? '';
    const searchFilter = search ? queryDto.stringifyFilter({ [QueryType.Search]: search }) : '';
    const query = searchFilter ? queryDto.build(paginationQuery, searchFilter) : paginationQuery;

    return projectApi.list(query).then(({ data, pagination }) => {
      dispatch(setPagination(pagination));
      dispatch(
        pushQuery({
          query: `${queryDto.stringifyPagination(pagination.index)}${languageQuery}${
            searchFilter ? `&${searchFilter}` : ''
          }`,
        })
      );

      return data;
    });
  },
  { serializeError }
);

export const fetchCurrentProject = createAsyncThunk(
  `${STORE_NAME}/fetchCurrentProject`,
  (_, { dispatch, getState }) => {
    const projectId = selectReportOrCurrentProjectId(getState());
    const language = selectLanguage(getState());

    return projectApi.get(projectId).then(async (project) => {
      const shouldRedirectToPulseTemplateChild = project.kind === ProjectKind.PULSE_TEMPLATE;
      if (shouldRedirectToPulseTemplateChild) {
        dispatch(replace(projectUrlBuilder.getDefault(project, language)));
        return ProjectState.INITIAL_DOMAIN.current.data;
      }

      if (Project.isPulseTemplateChild(project)) {
        const templateProjects = await projectApi.getTemplateProjects(project.pulseTemplateId);
        return {
          ...project,
          pulseProjects: templateProjects.pulses,
          pulseOnDemandProjects: templateProjects.onDemand,
          pulseLifecycleProjects: templateProjects.lifecycle,
        };
      }

      return project;
    });
  },
  { serializeError }
);

export const fetchReportAvailabilities = createAsyncThunk(
  `${STORE_NAME}/fetchReportAvailabilities`,
  (_, { getState }) => {
    const projectId = selectReportOrCurrentProjectId(getState());
    return projectApi.listReportAvailability(projectId);
  },
  { serializeError }
);

export const fetchQuestionsStats = createAsyncThunk(
  `${STORE_NAME}/fetchQuestionsStats`,
  (_, { getState }) => {
    const isPulse = selectIsPulseOrTemplateProjectKind(getState());
    const templateId = selectPulseTemplateId(getState());
    const projectId = selectCurrentProjectId(getState());

    return projectApi.getQuestionsStats(isPulse ? templateId! : projectId);
  },
  { serializeError }
);

export const fetchOverallSentimentState = createAsyncThunk(
  `${STORE_NAME}/fetchOverallSentimentState`,
  (_, { getState }) => {
    const currentProjectId = selectCurrentProjectId(getState());
    return projectApi.getSentimentState(currentProjectId);
  },
  { serializeError }
);

export const fetchSentimentState = createAsyncThunk(
  `${STORE_NAME}/fetchSentimentState`,
  (questionId: number | undefined, { getState }) => {
    const currentProjectId = selectCurrentProjectId(getState());
    return projectApi.getSentimentState(currentProjectId, questionId);
  },
  { serializeError }
);

export const fetchProjectLiveSettings = createAsyncThunk<ProjectLiveSettingsModel, number>(
  `${STORE_NAME}/fetchProjectLiveSettings`,
  (projectId) => projectApi.getLiveSettings(projectId),
  { serializeError }
);

export const fetchProjectKind = createAsyncThunk<ProjectKind, number>(
  `${STORE_NAME}/fetchProjectKind`,
  (projectId) => projectApi.getKindByProjectId(projectId),
  { serializeError }
);

export const fetchFocusAreas = createAsyncThunk<FocusAreasModel, number | undefined>(
  `${STORE_NAME}/fetchFocusAreas`,
  (departmentId, { getState }) => {
    const surveyId = selectCurrentProjectId(getState());
    return projectApi.getFocusAreas(surveyId, departmentId);
  },
  { serializeError }
);
